import Email from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import SmartLabel from "@/domain/enums/SmartLabel";
import { Tone } from "@/hooks/useGenerateAnswerWithIA";
import useGetNavbarData from "@/hooks/useGetNavbarData";
import useMailActions from "@/hooks/useMailActions";
import { setOpenedMail } from "@/stores/mailStore";
import { Editor } from "@tiptap/react";
import {
    Dispatch,
    RefObject,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from "react";
import CenteredSpinner from "../CenteredSpinner";
import { FocusTarget } from "../mailsList/MailAction";
import { ContextBarVariant } from "../mailThread/ContextBar";
import MailThread from "../mailThread/MailThread";
import Spinner from "../ui/spinner";
import DeepFocusNavigationBar, {
    DeepFocusNavigationBarDirection,
} from "./DeepFocusNavigationBar";

const INFINITE_SCROLL_THRESHOLD = 5;

export interface DeepFocusViewProps {
    mails: Array<Email>;
    loadNextPage: () => void;
    isLoading?: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    editor: Editor;
    mailInputExpanded: boolean;
    setMailInputExpanded: (
        inputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    toInputRef: RefObject<HTMLInputElement | null>;
    attachments: EmailAttachment[];
    setAttachments: Dispatch<SetStateAction<EmailAttachment[]>>;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
    isLoadingSuggestion: boolean;
    isPendingChange: boolean;
    makeSuggestion: (action: Tone) => void;
    pendingSuggestion: boolean;
    setPendingSuggestion: Dispatch<SetStateAction<boolean>>;
    suggestionEditor: Editor;
}

const DeepFocusView = ({
    mails,
    loadNextPage,
    isLoadingSuggestion,
    hasNextPage,
    isFetchingNextPage,
    editor,
    mailInputExpanded,
    setMailInputExpanded,
    toInputRef,
    attachments,
    setAttachments,
    acceptOrRefuseSuggestion,
    isLoading,
    isPendingChange,
    makeSuggestion,
    pendingSuggestion,
    setPendingSuggestion,
    suggestionEditor,
}: DeepFocusViewProps) => {
    const { unProcessedFocusMailCount } = useGetNavbarData();
    const [displayedMailIndex, setDisplayedMailIndex] = useState<number>(0);

    const { setProcessStatus } = useMailActions();

    const currentMail = useMemo(
        () => mails[displayedMailIndex],
        [mails, displayedMailIndex],
    );

    const handleMailChange = (direction: DeepFocusNavigationBarDirection) => {
        if (direction === DeepFocusNavigationBarDirection.LEFT) {
            const previousMail =
                displayedMailIndex > 0
                    ? displayedMailIndex - 1
                    : mails.length - 1;
            setDisplayedMailIndex(previousMail);
            setOpenedMail(mails[previousMail], { openIntent: false });
        } else {
            const nextMail =
                displayedMailIndex + 1 < mails.length
                    ? displayedMailIndex + 1
                    : 0;
            setDisplayedMailIndex(nextMail);
            setOpenedMail(mails[nextMail], { openIntent: false });
        }
    };

    const handleProcessingMail = () => {
        if (currentMail) {
            setProcessStatus.mutateAsync({
                mail: currentMail,
                isUserProcessed: !currentMail.isUserProcessed,
                listId: SmartLabel.Focus,
            });
        }
    };

    useEffect(() => {
        if (mails.length === 0 || !unProcessedFocusMailCount) {
            return;
        }

        if (
            hasNextPage &&
            !isFetchingNextPage &&
            mails.length - displayedMailIndex < INFINITE_SCROLL_THRESHOLD
        ) {
            loadNextPage();
        }
    }, [
        unProcessedFocusMailCount,
        displayedMailIndex,
        hasNextPage,
        isFetchingNextPage,
        mails.length,
        loadNextPage,
    ]);

    if (!unProcessedFocusMailCount) {
        return <CenteredSpinner size="large" />;
    }

    if (currentMail) {
        return (
            <div className="relative h-full w-full">
                {unProcessedFocusMailCount > 0 && (
                    <MailThread
                        acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
                        attachments={attachments}
                        classname="absolute bottom-0 left-0 right-0 top-0 px-11 pb-30 pt-24"
                        editor={editor}
                        handleProcessingFocusMail={handleProcessingMail}
                        isLoading={isLoadingSuggestion}
                        isPendingChange={isPendingChange}
                        listId={SmartLabel.Focus}
                        mail={currentMail}
                        mailInputExpanded={mailInputExpanded}
                        makeSuggestion={makeSuggestion}
                        pendingSuggestion={pendingSuggestion}
                        setAttachments={setAttachments}
                        setMailInputExpanded={setMailInputExpanded}
                        setPendingSuggestion={setPendingSuggestion}
                        suggestionEditor={suggestionEditor}
                        toInputRef={toInputRef}
                        variant={ContextBarVariant.DEEP_FOCUS}
                    />
                )}
                <div className="absolute bottom-0 left-0 right-0 h-19 border-t border-white/10 bg-white/10 px-11 py-3 backdrop-blur-md">
                    {isLoading && (
                        <Spinner className="absolute right-4 top-1/2 -translate-y-1/2" />
                    )}
                    <DeepFocusNavigationBar
                        displayedMailIndex={displayedMailIndex}
                        handleMailChange={handleMailChange}
                        handleProcessingMail={handleProcessingMail}
                        unprocessedMailCount={unProcessedFocusMailCount}
                    />
                </div>
            </div>
        );
    }

    return <CenteredSpinner size="large" />;
};

export default DeepFocusView;
